import {
  HttpClient,
  HttpParams,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { PagedResult } from '../models/common.models';
import { ResourceDto, ResourceListRequest } from '../models/resource.models';
import { PracticeArea } from '../models/user.models';

@Injectable({
  providedIn: 'root',
})
export class PracticeAreaService {
  private http = inject(HttpClient);

  private readonly apiUrl = `${environment.apiUrl}/api/practiceareas`;

  /**
   * Get all practice areas from the API.
   */
  getAllPracticeAreas(): Observable<PracticeArea[]> {
    return this.http.get<PracticeArea[]>(this.apiUrl).pipe(
      map((practiceAreas) =>
        practiceAreas.map((pa) => this.transformPracticeArea(pa))
      ),
    );
  }

  /**
   * Get a specific practice area by code
   */
  getPracticeAreaByCode(code: string): Observable<PracticeArea> {
    return this.http.get<PracticeArea>(`${this.apiUrl}/${code}`).pipe(
      map((practiceArea) => this.transformPracticeArea(practiceArea)),
    );
  }

  /**
   * Create a custom practice area and return it with its generated code.
   */
  createPracticeArea(name: string): Observable<PracticeArea> {
    return this.http.post<PracticeArea>(this.apiUrl, { name }).pipe(
      map((practiceArea) => this.transformPracticeArea(practiceArea)),
    );
  }

  /**
   * Transform practice area data from API format to frontend format
   */
  private transformPracticeArea(practiceArea: any): PracticeArea {
    return {
      practiceCode: practiceArea.practiceCode || practiceArea.code || '',
      description: practiceArea.description || '',
      originalDescription: practiceArea.originalDescription,
      isActive: practiceArea.isActive ?? true,
      isCustom: practiceArea.isCustom ?? false,
      assignedUsers:
        practiceArea.assignedUsers || practiceArea.AssignedUsers || [],
    };
  }

  /**
   * Add a user to a practice area
   */
  addUserToPracticeArea(
    userId: number,
    practiceAreaCode: string
  ): Observable<any> {
    return this.http
      .post(
        `${this.apiUrl}/users/${userId}/practice-areas/${practiceAreaCode}`,
        {}
      );
  }

  /**
   * Remove a user from a practice area
   */
  removeUserFromPracticeArea(
    userId: number,
    practiceAreaCode: string
  ): Observable<any> {
    return this.http
      .delete(
        `${this.apiUrl}/users/${userId}/practice-areas/${practiceAreaCode}`
      );
  }

  /**
   * Update the display name for a practice area
   */
  updatePracticeAreaDisplayName(
    practiceCode: string,
    displayName: string | null
  ): Observable<any> {
    return this.http
      .put(`${this.apiUrl}/${practiceCode}/display-name`, { displayName });
  }

  /**
   * Get active employees assigned to a practice area with server-side search and pagination.
   * Admin view — no access filter applied.
   */
  getAssignedResources(
    practiceAreaCode: string,
    page: number = 1,
    pageSize: number = 25,
    search?: string
  ): Observable<PagedResult<ResourceDto>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    if (search?.trim()) {
      params = params.set('search', search.trim());
    }
    return this.http
      .get<PagedResult<ResourceDto>>(
        `${this.apiUrl}/${practiceAreaCode}/resources`,
        { params },
      );
  }

  /**
   * Search active employees for admin practice-area management.
   * Bypasses the current user's practice-area access filter so admins can
   * find employees outside their own practice areas (including employees
   * without an application user account).
   */
  searchResourcesForAdmin(
    request: ResourceListRequest
  ): Observable<PagedResult<ResourceDto>> {
    const body: Record<string, unknown> = {
      page: request.page,
      pageSize: request.pageSize,
    };
    if (request.search) {
      body['search'] = request.search;
    }
    if (request.excludePracticeAreaCode) {
      body['excludePracticeAreaCode'] = request.excludePracticeAreaCode;
    }
    if (request.sortBy) {
      body['sortBy'] = request.sortBy;
    }
    if (request.sortDirection) {
      body['sortDirection'] = request.sortDirection;
    }
    return this.http
      .post<PagedResult<ResourceDto>>(`${this.apiUrl}/resources/search`, body);
  }

  /**
   * Add an employee to a practice area by employee ID (Source = Manual).
   * Does not require the employee to have an application user account.
   */
  addEmployeeToPracticeArea(
    employeeId: string,
    practiceAreaCode: string
  ): Observable<any> {
    return this.http
      .post(
        `${this.apiUrl}/employees/${employeeId}/practice-areas/${practiceAreaCode}`,
        {}
      );
  }

  /**
   * Remove an employee from a practice area by employee ID.
   * Does not require the employee to have an application user account.
   */
  removeEmployeeFromPracticeArea(
    employeeId: string,
    practiceAreaCode: string
  ): Observable<any> {
    return this.http
      .delete(
        `${this.apiUrl}/employees/${employeeId}/practice-areas/${practiceAreaCode}`
      );
  }
}
